/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { IContentLoaderProps } from 'react-content-loader';

import { useEventCallback } from '../../components/hooks/useEventCallback';
import { useObjectState } from '../../utils/useObjectState';

type State = {
  wWidth: number | undefined;
  wHeight: number | undefined;
};

type WrapperProps = {
  wrapperClass?: string;
};

export type WrapSkeletonInjectedProps = { skeletonWrapper: State };

export function wrapSkeleton<Props extends IContentLoaderProps = object>(
  Component: React.ComponentType<Props & WrapSkeletonInjectedProps>,
  wrapperStaticClass?: string,
): React.FunctionComponent<WrapperProps & Props> {
  return function (props) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const { wrapperClass, ...otherProps } = props;
    const [state, setState] = useObjectState<State>({
      wHeight: undefined,
      wWidth: undefined,
    });

    const onResize = useEventCallback(() => {
      if (wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        const wrapperHeight = wrapperRef.current.clientHeight;
        if (wrapperWidth !== state.wWidth || wrapperHeight !== state.wHeight) {
          setState({ wWidth: wrapperWidth, wHeight: wrapperHeight });
        }
      }
    });

    useLayoutEffect(() => {
      onResize();
    }, [onResize]);

    useEffect(() => {
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('resize', onResize);
      };
    }, [onResize]);

    return (
      <div ref={wrapperRef} className={cx(wrapperClass, wrapperStaticClass)} style={{ lineHeight: '0' }}>
        <Component skeletonWrapper={state} {...(otherProps as Props)} />
      </div>
    );
  };
}
